import React, {useState, useEffect} from "react";
import {
    Typography,
    Grid,
    Pagination,
    Box
} from "@mui/material";
import BatchFilesPage from "./BatchFilesPage";
import {
    fetchBatches, loadBatches as loadBatchesList,
    getGlobalMetrics
} from "../../axios-client";
import {io} from "socket.io-client";
import PropTypes from "prop-types";
import SectionHeaderWithButton from "../../reusableComponents/Titles/SectionHeaderWithButton";
import MetricsCard from "./MetricsCard";
import BatchCard from "./BatchCard";
import SoftBox from "components/SoftBox";
import { MenuItem, Select } from "@mui/material";
import {useSoftUIController} from "../../context";
import DeleteBatchDialog from "./DeleteBatchDialog";
import CircularProgress from '@mui/material/CircularProgress';
import {SUPER_ADMIN} from "../../constants/constants";

function BatchesPage({ setNotification }) {
    const [controller] = useSoftUIController();
    const { direction, role} = controller;
    const [metrics, setMetrics] = useState({});
    const [batches, setBatches] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [batchToDelete, setBatchToDelete] = useState(null);

    const [socket, setSocket] = useState(null);

    const loadBatches = async (pageNum = 1) => {
        try {
            setLoading(true);
            const res = await fetchBatches(pageNum, pageSize);
            setBatches(res.data.items || []);
            setTotalPages(Math.ceil(res.data.total / res.data.page_size));
            setPage(pageNum);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadMetrics = async () => {
        try {
            const res = await getGlobalMetrics();
            setMetrics(res.data || []);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        loadMetrics();
    }, []);

     useEffect(() => {
        const baseURL = window?.appConfig?.REACT_APP_SOCKET_SERVER || process.env.REACT_APP_SOCKET_SERVER;
        const s = io(baseURL, { path: "/socket.io" });
        setSocket(s);
        loadBatches(1);
        return () => s.disconnect();
    }, [pageSize]);

    useEffect(() => {
        if (!socket) return;
        batches.forEach((batch) => {
            if (["in_progress", "partially_done", "partially_delivered"].includes(batch.status)) {
                const eventName = `batch_status_update-${batch.id}`;
                socket.on(eventName, (data) => {
                    setBatches((prev) =>
                        prev.map((b) =>
                            b.id === batch.id ?
                                {
                                    ...b,
                                    status: data.status,
                                    delivery_daemon_status: data.delivery_daemon_status,
                                    daemon_status: data.daemon_status,
                                }
                                : b
                        )
                    );
                });
            }
        });

        return () => {
            batches.forEach((batch) => {
                socket.off(`batch_status_update-${batch.id}`);
            });
        };
    }, [batches, socket]);

    const onViewFiles = (batch) => setSelectedBatch(batch);

    const onBack = () => {
        setSelectedBatch(null);
        loadBatches(page);
    };
    const handlePaginationSizeChange = (event) => {
        let newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
    };

    const onLoadBatch = async () => {
        try {
            const res = await loadBatchesList();
            loadBatches()
            setNotification({ show: true, snackbarMessage: res.data.message, severity: 'success' });
        } catch (err) {
            console.error(err);
            setNotification({ show: true, snackbarMessage: 'An unexpected error occurred', severity: 'error' });
        }
    };

    if (selectedBatch) {
        return <BatchFilesPage />;
    }

    const onDeleteBatch = (batch) => {
        setBatchToDelete(batch);
        setOpenDeleteDialog(true);
    };

    const closeDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setBatchToDelete(null);
    };

    return (
        <Box sx={{p: 4}}>

            {role === SUPER_ADMIN &&
                <React.Fragment>
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                        Global Metrics
                    </Typography>
                    <MetricsCard metrics={metrics} />
                </React.Fragment>
            }

            {role === SUPER_ADMIN && <SectionHeaderWithButton
                title="Batches"
                buttonText="Load Batches"
                loading={loading}
                onClick={onLoadBatch}
            />}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    loading batches....
                    <CircularProgress
                        size={24}
                        thickness={4}
                        style={{ color: "#6c6c6c", marginLeft: 8 }}
                    />
                </Box>
            ) : (batches.length === 0) ? (    
                <Box sx={{ mt: 4, textAlign: "center", color: "#888" }}>
                    No batches found.
                </Box>
            ) : (
                <>
                    <Grid container spacing={4}>
                        {batches.map((batch) => (
                            <Grid item xs={12} sm={6} md={4} key={batch.id}>
                                <BatchCard
                                    batch={batch}
                                    onViewFiles={onViewFiles}
                                    onDeleteBatch={onDeleteBatch}
                                    role={role}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    <SoftBox style={{display: "flex", float: direction === "rtl" ? "left" : "right"}}>
                        <SoftBox>
                            <Select
                                id="rowsPerPage"
                                fullWidth
                                value={pageSize}
                                onChange={handlePaginationSizeChange}
                                style={{
                                    width: "100%",
                                    padding: "10px 0px",
                                    border: "none",
                                    fontSize: "14px",
                                    boxSizing: "border-box",
                                    outline: "none",
                                    fontFamily: "Arial, Roboto",
                                }}
                                inputProps={{"aria-label": "Without label"}}
                            >
                                <MenuItem key={"6"} value="6">6</MenuItem>
                                <MenuItem key={"12"} value="12" selected={true}>12</MenuItem>
                                <MenuItem key={"24"} value="24">24</MenuItem>
                            </Select>
                        </SoftBox>
                        <SoftBox dir={direction}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => loadBatches(value)}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </SoftBox>
                    </SoftBox>
                </>
            )}

            {openDeleteDialog && batchToDelete && (
                <DeleteBatchDialog
                    isModalOpen={openDeleteDialog}
                    handleCloseModal={closeDeleteDialog}
                    batchId={batchToDelete.id}
                    batchName={batchToDelete.name}
                    refreshBatches={() => loadBatches(page)}
                    setNotification={setNotification}
                />
                )}
        </Box>
    );
}

BatchesPage.propTypes = {
    setNotification: PropTypes.func.isRequired,
};

export default BatchesPage;